import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import AuthService, { notifyAuthChanged } from "../../services/AuthService";
import Logger from "../../services/Logger";
import { usePagesTranslation } from "../locales";
import { trackEvent } from "../../services/GoogleAnalytics";
import { SITE_BG } from "../../dark-theme/palette";
import { isGameHost, normalizeAuthReturnPath } from "../../config/gameHost";

const AUTH_RETURN_KEY = "puzzleAuthReturnTo";

function resolveReturnPath(location) {
    if (typeof location.state?.from === "string" && location.state.from) {
        return normalizeAuthReturnPath(location.state.from);
    }
    if (location.state?.from?.pathname) {
        return normalizeAuthReturnPath(location.state.from.pathname);
    }
    try {
        const stored = sessionStorage.getItem(AUTH_RETURN_KEY);
        if (stored) {
            return normalizeAuthReturnPath(stored);
        }
    } catch {
        /* ignore */
    }
    return normalizeAuthReturnPath("/");
}

/**
 * Immediately sends users to Cognito managed login (no local sign-in/sign-up chooser).
 * If the URL already contains an OAuth authorization code, waits for Amplify to finish and redirects home (or saved return path).
 */
export default function Login() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState("");
    const [status, setStatus] = useState("redirect"); // redirect | completing

    const from = resolveReturnPath(location);

    useEffect(() => {
        trackEvent("entered", { name: "Signin" });
    }, []);

    useEffect(() => {
        const sp = new URLSearchParams(window.location.search);
        const oauthReturn = sp.has("code") && sp.has("state");

        if (oauthReturn) {
            setStatus("completing");
            let attempts = 0;
            const maxAttempts = 80;
            let cancelledOauth = false;

            const tryFinish = async () => {
                if (cancelledOauth) return;
                try {
                    if (await AuthService.isAuthenticated()) {
                        notifyAuthChanged();
                        try {
                            sessionStorage.removeItem(AUTH_RETURN_KEY);
                        } catch {
                            /* ignore */
                        }
                        navigate(from, { replace: true });
                        return;
                    }
                } catch (e) {
                    Logger.error("OAuth completion check:", e);
                }
                attempts += 1;
                if (attempts >= maxAttempts) {
                    if (!cancelledOauth) {
                        setError(t("login.error-generic"));
                        setStatus("idle");
                    }
                    return;
                }
                window.setTimeout(tryFinish, 250);
            };

            tryFinish();
            return () => {
                cancelledOauth = true;
            };
        }

        let cancelled = false;
        (async () => {
            try {
                if (await AuthService.isAuthenticated()) {
                    if (!cancelled) navigate(from, { replace: true });
                    return;
                }
            } catch (e) {
                Logger.error("Login auth check:", e);
            }

            try {
                sessionStorage.setItem(AUTH_RETURN_KEY, normalizeAuthReturnPath(from));
            } catch {
                /* ignore */
            }

            if (cancelled) return;
            setStatus("redirect");
            try {
                const result = await AuthService.loginWithRedirect();
                if (result?.alreadyAuthenticated && !cancelled) {
                    navigate(from, { replace: true });
                }
            } catch (err) {
                Logger.error("Login redirect error:", err);
                if (!cancelled) {
                    setError(err?.message || t("login.error-generic"));
                    setStatus("idle");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [navigate, from, t]);

    const message =
        status === "completing" ? t("login.completing-sign-in") : status === "redirect" ? t("login.redirecting") : "";

    return (
        <Box
            sx={{
                boxSizing: "border-box",
                minHeight: "100dvh",
                padding: "max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: SITE_BG,
            }}
        >
            {!error && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <CircularProgress sx={{ color: "#3BA666" }} />
                    {message && (
                        <Typography variant="body2" color="text.secondary">
                            {message}
                        </Typography>
                    )}
                </Box>
            )}
            {error && (
                <Typography color="error" variant="body2" sx={{ textAlign: "center", maxWidth: 360, px: 2 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}
